/** The activity record's Usage tab (plan §3, §10.4, §9.5): every record that uses the activity,
 *  in the "Where it's used" panel's groups — Tours · Catalog itineraries · Orphan day plans ·
 *  Unlisted trips · Line-set stops · Nearby of — each paged in place, each row as the record
 *  tab draws it (the day it is on, a stop's distance, "blocks Delete" for what disables or
 *  refuses the delete). Under the header, the tours' upcoming departures and bookings from the used-by
 *  summary — asked for only once the panel has been on screen (D7), the same query the record
 *  tab's band reads. The totals are the record tab's first used-by page (one cache). */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import * as activitiesApi from '@/api/activities';
import type { ActivityDetail, ActivityUsedByRef } from '@/api/activities';
import {
  activityUsedBySummaryKey,
  activityWhereUsedKey,
  useActivityUsedBySummary,
  WHERE_USED_FIRST_PAGE_SIZE,
} from '@/api/activityRecord';
import { USAGE_PAGE_SIZE, UsagePanel, UsageSummaryLine, type UsagePageQuery } from '@/features/catalog/usage/UsagePanel';
import { usageSummaryText, type UsageGroupDef } from '@/features/catalog/usage/usageModel';
import { useSeenOnce } from '@/features/records/page/useSeenOnce';

import { whereUsedPageKey } from './WhereUsedGroupDrawer';
import { WhereUsedRow } from './WhereUsedRow';
import {
  WHERE_USED_EMPTY,
  WHERE_USED_GROUPS,
  WHERE_USED_LABEL,
  WHERE_USED_NOUN,
  WHERE_USED_UNLISTED,
  type WhereUsedGroupKey,
} from './whereUsedModel';

/** The Usage tab's groups: the record tab's, in its order. */
export const ACTIVITY_USAGE_GROUPS: readonly UsageGroupDef<WhereUsedGroupKey>[] = WHERE_USED_GROUPS.map((key) => ({
  key,
  label: WHERE_USED_LABEL[key],
  noun: WHERE_USED_NOUN[key],
  ...(key === 'demo_user_itinerary' ? { unlisted: WHERE_USED_UNLISTED } : {}),
}));

const n = (v: number) => v.toLocaleString('en-US');
const toursText = (count: number, capped: boolean) =>
  capped ? `${n(count)}+ tours` : `${n(count)} ${count === 1 ? 'tour' : 'tours'}`;

/** One page of one relation under a search; with none, the relation drawer's own key (one
 *  cache). */
export function activityUsagePage(
  activityId: number,
  group: WhereUsedGroupKey,
  page: number,
  search: string,
): UsagePageQuery<ActivityUsedByRef> {
  const key = whereUsedPageKey(activityId, group, page);
  return {
    queryKey: search ? [...key, search] : key,
    queryFn: () =>
      activitiesApi.usedBy(activityId, {
        type: group,
        page,
        pageSize: USAGE_PAGE_SIZE,
        ...(search ? { search } : {}),
      }),
  };
}

export function ActivityUsageTab({ detail }: { detail: Pick<ActivityDetail, 'id' | 'timezone'> }) {
  const id = detail.id;
  const qc = useQueryClient();
  const [panelRef, seen] = useSeenOnce<HTMLElement>();
  // The record tab's first used-by page: its `available_types` are every relation's total.
  const typesQ = useQuery({
    queryKey: activityWhereUsedKey(id),
    queryFn: () => activitiesApi.usedBy(id, { pageSize: WHERE_USED_FIRST_PAGE_SIZE }),
  });
  const summaryQ = useActivityUsedBySummary(id, { enabled: seen });
  const summary = summaryQ.data;

  const pageQuery = useCallback(
    (group: WhereUsedGroupKey, page: number, search: string) => activityUsagePage(id, group, page, search),
    [id],
  );
  const renderRow = useCallback(
    (group: WhereUsedGroupKey, row: ActivityUsedByRef) => <WhereUsedRow key={`${row.type_key}:${row.id}`} group={group} row={row} />,
    [],
  );
  // Refresh: every used-by page (the totals and each relation's) and the summary line.
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['activities', 'usedBy', id] });
    void qc.invalidateQueries({ queryKey: activityUsedBySummaryKey(id) });
  };

  return (
    <UsagePanel
      panelRef={panelRef}
      groups={ACTIVITY_USAGE_GROUPS}
      types={typesQ.data?.available_types}
      typesFailed={typesQ.isError}
      onRefresh={refresh}
      pageQuery={pageQuery}
      renderRow={renderRow}
      empty={WHERE_USED_EMPTY}
      what="this activity"
      summary={
        <UsageSummaryLine
          text={summary ? usageSummaryText(summary, { tours: toursText, none: 'No tours use it', zone: detail.timezone }) : null}
          computedAt={summary?.computed_at}
          failed={summaryQ.isError}
          onRetry={() => void summaryQ.refetch()}
        />
      }
    />
  );
}
